/**
 * @file api.ts
 * @brief Activity log feed: who deleted / changed what, newest first.
 *
 * @details
 * One hook for both shells. `company` reads GET /api/company/activity (own
 * company, Super Admin shown as "VERBLIZR Support"); `platform` reads
 * GET /api/admin/activity (every company, real names, optional company
 * filter). The backend pages by cursor, so this is an infinite query with a
 * "Load more" button, not page numbers.
 *
 * @references
 * - BACKEND/src/features/activity/activityLog.js
 *
 * @author DES
 * @date 05-10-2026
 */

import { useInfiniteQuery } from "@tanstack/react-query";
import { z } from "zod";
import { apiGet } from "@/shared/api/client";
import { adminEndpoints, companyEndpoints } from "@/shared/api/endpoints";

export type ActivityScope = "company" | "platform";

const ActivityEventSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  company_id: z.string().nullable(),
  company_name: z.string().nullable().optional(),
  actor_user_id: z.string().nullable(),
  actor_role: z.string().nullable(),
  actor_name: z.string().nullable(),
  actor_email: z.string().nullable(),
  action: z.string(),
  target_type: z.string().nullable(),
  target_id: z.string().nullable(),
  target_label: z.string().nullable(),
  details: z.record(z.unknown()).nullable(),
});

const ActivityPageSchema = z.object({
  events: z.array(ActivityEventSchema),
  nextBefore: z.string().nullable(),
});

export type ActivityEvent = z.infer<typeof ActivityEventSchema>;

const PAGE_SIZE = 50;

export function useActivityLog(scope: ActivityScope, companyId: string | null) {
  return useInfiniteQuery({
    queryKey: ["activity-log", scope, companyId ?? "all"] as const,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const usp = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (pageParam) usp.set("before", pageParam);
      if (scope === "platform" && companyId) usp.set("companyId", companyId);
      const base =
        scope === "platform" ? adminEndpoints.activity : companyEndpoints.activity;
      const raw = await apiGet<unknown>(`${base}?${usp.toString()}`);
      return ActivityPageSchema.parse(raw);
    },
    getNextPageParam: (last) => last.nextBefore,
  });
}
